import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  format, addWeeks, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO, addDays,
} from "date-fns";
import { it } from "date-fns/locale";

export default function AgendaCollaboratore() {
  const [collaborators, setCollaborators] = useState([]);
  const [selected, setSelected] = useState("");
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);

  const range = useMemo(() => ({
    start: startOfWeek(cursor, { weekStartsOn: 1 }),
    end: endOfWeek(cursor, { weekStartsOn: 1 }),
  }), [cursor]);

  useEffect(() => {
    api.get("/collaborators").then(({ data }) => {
      setCollaborators(data);
      setSelected((prev) => prev || (data[0]?.id || ""));
    }).catch(() => toast.error("Errore caricamento collaboratori"));
  }, []);

  const load = useCallback(async () => {
    if (!selected) return;
    try {
      const params = {
        start: range.start.toISOString(),
        end: addDays(range.end, 1).toISOString(),
        collaborator_id: selected,
      };
      const { data } = await api.get("/appointments", { params });
      setAppts(data);
    } catch { toast.error("Errore caricamento agenda"); }
  }, [selected, range]);
  useEffect(() => { load(); }, [load]);

  const weekDays = eachDayOfInterval({ start: range.start, end: range.end });
  const apptsForDay = (day) =>
    appts.filter((a) => isSameDay(parseISO(a.inizio), day)).sort((x, y) => x.inizio.localeCompare(y.inizio));
  const col = collaborators.find((c) => c.id === selected);

  return (
    <div className="space-y-6">
      <PageHeader title="Agenda Collaboratori" subtitle="Seleziona un collaboratore e consulta il suo calendario settimanale" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-full sm:w-[300px] bg-white" data-testid="agenda-collaborator-select">
            <SelectValue placeholder="Seleziona collaboratore" />
          </SelectTrigger>
          <SelectContent>
            {collaborators.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.nome} {c.cognome}{c.team ? ` — ${c.team}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor((c) => addWeeks(c, -1))} data-testid="agenda-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Oggi</Button>
          <Button variant="outline" size="icon" onClick={() => setCursor((c) => addWeeks(c, 1))} data-testid="agenda-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground" data-testid="agenda-week-summary">
        {col ? (
          <>
            {col.nome} {col.cognome} · settimana {format(range.start, "d MMM", { locale: it })} – {format(range.end, "d MMM yyyy", { locale: it })} · <strong>{appts.length}</strong> appuntamenti
          </>
        ) : "Nessun collaboratore selezionato"}
      </p>

      <Card className="p-4 border-slate-200 overflow-x-auto">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3 min-w-[720px]">
          {weekDays.map((day) => {
            const list = apptsForDay(day);
            return (
              <div key={day.toISOString()} className="rounded-lg border border-slate-100 bg-slate-50/40" data-testid={`agenda-day-${format(day, "yyyy-MM-dd")}`}>
                <div className={`px-3 py-2 border-b border-slate-100 ${isSameDay(day, new Date()) ? "bg-primary/5" : ""}`}>
                  <div className="text-xs uppercase text-slate-400">{format(day, "EEE", { locale: it })}</div>
                  <div className={`text-sm font-semibold ${isSameDay(day, new Date()) ? "text-primary" : "text-slate-700"}`}>{format(day, "d MMM", { locale: it })}</div>
                </div>
                <div className="p-2 space-y-1.5 min-h-[90px]">
                  {list.length === 0 ? (
                    <p className="text-[11px] text-slate-300 text-center py-3">—</p>
                  ) : list.map((a) => (
                    <div key={a.id} className="rounded-md px-2 py-1.5 text-xs" data-testid={`agenda-appt-${a.id}`}
                         style={{ backgroundColor: `${a.collaborator_colore || "#17B8B0"}1a`, borderLeft: `3px solid ${a.collaborator_colore || "#17B8B0"}` }}>
                      <div className="font-medium text-slate-800">
                        <span className="font-mono-data">{format(parseISO(a.inizio), "HH:mm")}</span> {a.client_nome}
                      </div>
                      {a.service_nome && <div className="truncate text-[11px] text-slate-500">{a.service_nome}</div>}
                      {a.stanza && <div className="truncate text-[10px] text-slate-400">{a.stanza}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
